import React, { useState, useEffect } from 'react';
import { ChatMessage } from './types/chat';
import axios from 'axios';
import Sidebar from './components/Sidebar';
import { API_CONFIG } from './config';

function App() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [chatId, setChatId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Generate a chat ID when the component mounts
  useEffect(() => {
    setChatId(crypto.randomUUID());
  }, []);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const startNewChat = () => {
    setMessages([]);
    setChatId(crypto.randomUUID());
  };

  const sendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputMessage.trim()) return;

    const newMessage: ChatMessage = {
      message: inputMessage,
      timestamp: new Date().toISOString(),
      sender: 'user',
      chatId: chatId || undefined
    };

    setMessages([...messages, newMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const response = await axios.post(`${API_CONFIG.baseUrl}${API_CONFIG.endpoints.chat}`, newMessage);
      setMessages(prev => [...prev, response.data]);
      
      // Save the chatId from the response for future messages
      if (response.data.chatId && !chatId) {
        setChatId(response.data.chatId);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      // Add an error message
      setMessages(prev => [
        ...prev, 
        {
          message: "Sorry, there was an error processing your request. Please try again.",
          timestamp: new Date().toISOString(),
          sender: 'AI'
        }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex h-screen bg-white">
      {/* Sidebar */}
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} onNewChat={startNewChat} />
      
      {/* Main Content */}
      <div className={`flex flex-col flex-1 transition-all duration-300 ${sidebarOpen ? 'md:ml-64' : 'md:ml-16'}`}>
        {/* Header */}
        <header className="bg-white border-b border-gray-200 p-4 flex items-center justify-between">
          <div className="flex items-center">
            <button 
              onClick={toggleSidebar}
              className="text-gray-700 mr-4 focus:outline-none md:hidden"
            >
              ☰
            </button>
            <h1 className="text-gray-800 text-xl font-medium">Document-Aware Chat</h1>
          </div>
          <button 
            onClick={startNewChat}
            className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-md text-sm hover:bg-emerald-200 transition-colors"
          >
            New Chat
          </button>
        </header>

        {/* Chat Window */}
        <div className="flex-1 overflow-y-auto p-4 bg-white">
          <div className="max-w-3xl mx-auto">
            {messages.length === 0 && (
              <div className="text-center text-gray-500 mt-10">
                <p className="text-xl font-medium">How can I help you today?</p>
                <p className="mt-2">Ask me questions about the documents I have access to.</p>
                <div className="mt-6 p-4 bg-gray-50 rounded-lg text-sm text-gray-700 max-w-md mx-auto">
                  <p className="font-medium mb-2">Example questions:</p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>What is RAG?</li>
                    <li>Explain the benefits of document-aware chatbots</li>
                    <li>How does LangChain4j help with implementing RAG?</li>
                  </ul>
                </div>
              </div>
            )}
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`mb-6 ${
                  msg.sender === 'user' ? 'text-right' : 'text-left'
                }`}
              >
                <div className="flex items-start mb-1">
                  {msg.sender !== 'user' && (
                    <div className="w-8 h-8 rounded-full bg-emerald-500 text-white flex items-center justify-center mr-2 flex-shrink-0">
                      A
                    </div>
                  )}
                  <div
                    className={`inline-block p-3 rounded-lg max-w-[80%] ${
                      msg.sender === 'user'
                        ? 'bg-emerald-500 text-white'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    <div className="whitespace-pre-wrap">{msg.message}</div>
                  </div>
                  {msg.sender === 'user' && (
                    <div className="w-8 h-8 rounded-full bg-gray-300 text-gray-700 flex items-center justify-center ml-2 flex-shrink-0">
                      U
                    </div>
                  )}
                </div>
                <div className={`text-xs text-gray-500 ${msg.sender === 'user' ? 'pr-10' : 'pl-10'}`}>
                  {new Date(msg.timestamp).toLocaleTimeString()}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex items-center mb-6">
                <div className="w-8 h-8 rounded-full bg-emerald-500 text-white flex items-center justify-center mr-2 flex-shrink-0">
                  A
                </div>
                <div className="bg-gray-100 text-gray-800 p-3 rounded-lg">
                  <div className="flex space-x-2">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Message Input */}
        <div className="p-4 border-t border-gray-200 bg-white">
          <form onSubmit={sendMessage} className="max-w-3xl mx-auto relative">
            <input
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              className="w-full p-3 pr-16 border border-gray-300 rounded-lg focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
              placeholder="Ask about the documents..."
              disabled={isLoading}
            />
            <button
              type="submit"
              className={`absolute right-3 top-1/2 transform -translate-y-1/2 p-2 ${
                !inputMessage.trim() || isLoading ? 'text-gray-400' : 'text-emerald-500 hover:text-emerald-600'
              } focus:outline-none`}
              disabled={!inputMessage.trim() || isLoading}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13a1 1 0 102 0V9.414l1.293 1.293a1 1 0 001.414-1.414z" clipRule="evenodd" transform="rotate(90, 10, 10)" />
              </svg>
            </button>
          </form>
          <p className="text-xs text-gray-500 text-center mt-2">
            This AI assistant uses document retrieval to provide accurate answers. Results may vary based on available documents.
          </p>
        </div>
      </div>
    </div>
  );
}

export default App;
